import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Box, Heading, SimpleGrid, HStack, Text } from "@chakra-ui/react";
import { DonationBreakdown } from "../../../api/clientUser/getClientAnalytics";

ChartJS.register(ArcElement, Tooltip, Legend);

interface DonationBreakdownChartProps {
  breakdown: DonationBreakdown;
}

export const DonationBreakdownChart = ({ breakdown }: DonationBreakdownChartProps) => {
  // One-time vs Recurring data
  const typeData = {
    labels: ["One-time", "Recurring"],
    datasets: [
      {
        data: [breakdown.oneTime, breakdown.recurring],
        backgroundColor: ["#339CFF", "#00C851"],
        borderColor: ["#fff", "#fff"],
        borderWidth: 3,
      },
    ],
  };

  // Anonymous vs Named data
  const privacyData = {
    labels: ["Named", "Anonymous"],
    datasets: [
      {
        data: [breakdown.named, breakdown.anonymous],
        backgroundColor: ["#9333EA", "#FF6B35"],
        borderColor: ["#fff", "#fff"],
        borderWidth: 3,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          font: {
            size: 13,
            weight: "bold",
          },
          color: "#1A202C",
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: "rgba(26, 32, 44, 0.95)",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        borderColor: "#339CFF",
        borderWidth: 1,
      },
    },
  };

  return (
    <Box
      bg="white"
      p={{ base: 4, md: 8 }}
      borderRadius={{ base: "16px", md: "24px" }}
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      border="1px solid"
      borderColor="gray.100"
    >
      <Heading size={{ base: "sm", md: "md" }} color="#1A202C" fontWeight="700" mb={4}>
        Donation Breakdown
      </Heading>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 8 }}>
        <Box>
          <Text fontSize="sm" fontWeight="600" color="gray.600" textAlign="center" mb={4}>
            Payment Type
          </Text>
          <Doughnut data={typeData} options={options} />
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="600" color="gray.600" textAlign="center" mb={4}>
            Donor Privacy
          </Text>
          <Doughnut data={privacyData} options={options} />
        </Box>
      </SimpleGrid>

      {/* Donation Tiers */}
      <Box mt={8} pt={6} borderTop="1px solid" borderColor="gray.100">
        <Text fontSize="sm" fontWeight="600" color="gray.600" mb={4}>
          Donation Tiers
        </Text>
        {breakdown.byTier.map((tier, index) => (
          <HStack key={index} justify="space-between" py={2}>
            <Text fontSize="sm" color="gray.700" fontWeight="500">
              {tier.tier}
            </Text>
            <HStack spacing={4}>
              <Text fontSize="sm" color="gray.500">
                {tier.count} donors
              </Text>
              <Text fontSize="sm" fontWeight="700" color="#1A202C">
                ${(tier.amount / 100).toLocaleString()}
              </Text>
            </HStack>
          </HStack>
        ))}
      </Box>
    </Box>
  );
};
